import type { ReactNode } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { DropdownButton } from '@hivebrite/design-system/Dropdown';
import type { IDropdownItem } from '@hivebrite/design-system/Dropdown';
import {
  CATEGORIES,
  FontAwesomeIcon,
} from '@hivebrite/design-system/FontAwesomeIcon';
import { zIndices } from '@hivebrite/design-system/Theme';

import { VIEW_TYPE } from 'hb-react/shared/apps/Events/containers/Index/constants';
import { useEventViewType } from 'hb-react/shared/apps/Events/containers/Index/hooks/useEventViewType';

const DropdownItem = ({ icon, label, iconHeight = '16px', dataTestId }) => (
  <DropDownItemStyled data-testid={dataTestId}>
    <Box width="16px" justifyContent="center" mr={3} color="inherit">
      <FontAwesomeIcon
        height={iconHeight}
        iconName={icon}
        iconType={CATEGORIES.LIGHT}
      />
    </Box>
    <span>{label}</span>
  </DropDownItemStyled>
);

const CalendarItem = () => (
  <DropdownItem
    icon="calendar-day"
    label={__('Calendar')}
    dataTestId="toggle-type-calendar"
  />
);

const MapItem = () => (
  <DropdownItem
    icon="map-location-dot"
    label={__('Map')}
    iconHeight="15px"
    dataTestId="toggle-type-map"
  />
);

const ListItem = () => (
  <DropdownItem
    icon="list-ul"
    label={__('List')}
    dataTestId="toggle-type-calendar"
  />
);

const DropDownItems: DropdownItemType[] = [
  {
    view: VIEW_TYPE.CALENDAR as ViewType,
    label: <CalendarItem />,
  },
  {
    view: VIEW_TYPE.LIST as ViewType,
    label: <ListItem />,
  },
  {
    view: VIEW_TYPE.MAP as ViewType,
    label: <MapItem />,
  },
];

type ViewType = keyof typeof VIEW_TYPE;

type DropdownItemType = Omit<IDropdownItem, 'label'> & {
  view: ViewType;
  label: ReactNode;
};

const CurrentItem = ({
  view,
  items = [],
}: {
  view: ViewType;
  items: DropdownItemType[];
}) => {
  const item = items.find((item) => item.view === view);

  if (!item?.label) {
    return null;
  }

  return <>{item.label}</>;
};

export const ViewTypeToggle = () => {
  const { view, setView, availableViews } = useEventViewType();

  const items = DropDownItems.filter((item) =>
    availableViews.includes(item.view),
  );

  if (availableViews.length <= 1) {
    return null;
  }

  return (
    <Box zIndex={zIndices.popover}>
      <DropdownButton
        // @ts-expect-error: fix types
        items={items}
        // @ts-expect-error: fix types
        onClickItem={({ view }) => setView(view)}
        dataTestId="toggle-view-type"
      >
        <CurrentItem items={items} view={view as ViewType} />
      </DropdownButton>
    </Box>
  );
};

const DropDownItemStyled = styled.span`
  display: flex;
`;
